'use client';
import React, {
    MutableRefObject,
    PointerEvent,
    TouchEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import Typography from '@/components/shared/typography/typography.component';
import { useTranslation } from '@/hook';
import { numberWithCommas } from '@/utils';
import useDebounce from '@/hook/debounce.hook';
import { slugify } from '@/utils/filterUtil';

export interface TFilterGroupRangeProps {
    data: {
        min: number;
        max: number;
    };
    range: number[];
    filterType: 'slider';
    rangeChanged: (range: number[]) => void;
    label: string;
    vertical: boolean;
}
enum SLIDER_CONTROLS {
    VERTICAL1 = 'VERTICAL1',
    VERTICAL2 = 'VERTICAL2',
    HORIZONTAL1 = 'HORIZONTAL1',
    HORIZONTAL2 = 'HORIZONTAL2',
}
const FilterGroupSlider = ({
    data,
    label,
    rangeChanged,
    vertical = false,
    range,
}: TFilterGroupRangeProps) => {
    const { t } = useTranslation();

    const getValRelevant = (val: number) =>
        Math.round(((val - data.min) / (data.max - data.min)) * 10000) / 100;
    const getAbsDiff = (val1: number, val2: number) =>
        Math.round(Math.abs(val1 - val2));
    const getAbsVal = (val: number, roundingTo = 1000) => {
        return (
            Math.round(
                (((data.max - data.min) * val) / 100 + data.min) / roundingTo
            ) * roundingTo
        );
    };

    const [positionRight, setPositionRight] = useState(
        getValRelevant(range[0])
    );
    const [positionTop, setPositionTop] = useState(getValRelevant(range[0]));

    const [positionLeft, setPositionLeft] = useState(getValRelevant(range[1]));
    const [positionBottom, setPositionBottom] = useState(
        getValRelevant(range[1])
    );

    const updateMinArm = (val: number) => {
        setPositionRight(val);
        setPositionTop(val);
    };
    const updateMaxArm = (val: number) => {
        setPositionLeft(val);
        setPositionBottom(val);
    };

    const [markerSize, setMarkerSize] = useState(
        getAbsDiff(getValRelevant(range[0]), getValRelevant(range[1]))
    );

    const rightHand = useRef<HTMLDivElement>(null);
    const leftHand = useRef<HTMLDivElement>(null);
    const trackerRef = useRef<HTMLDivElement>(null);
    const trackerVerticalRef = useRef<HTMLDivElement>(null);

    const edgeInControl = useRef(null);

    const clientX = useRef(null);
    const clientY = useRef(null);

    const lastRight = useRef<number>(null);
    const lastLeft = useRef<number | null>(null);

    const lastTop = useRef<number | null>(null);
    const lastBottom = useRef<number | null>(null);
    const updateChanges = useDebounce((val1: number, val2: number) => {
        const vals = [getAbsVal(val1), getAbsVal(val2)].sort((a, b) => a - b);
        rangeChanged(vals);
    }, 300);

    useEffect(() => {
        if (!rightHand.current || !leftHand.current) return;

        document.addEventListener('pointermove', handleTouchMove, {
            passive: false,
            capture: true,
        });
        document.addEventListener('touchmove', handleTouchMove, {
            passive: false,
            capture: true,
        });
        document.addEventListener('pointerup', handleTouchEnd);
        document.addEventListener('touchend', handleTouchEnd, {
            passive: false,
            capture: true,
        });
        return () => {
            document.removeEventListener('pointermove', handleTouchMove);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('pointerup', handleTouchEnd);
            document.removeEventListener('touchend', handleTouchEnd, {
                capture: true,
            });
        };
    }, [rightHand.current]);

    const formatDisplayedValue = (value: number) => {
        const formatedNumber = numberWithCommas(value);
        return `${t('EGP')} ${formatedNumber}`;
    };

    //
    function isTouchEvent(
        e: React.SyntheticEvent | Event
    ): e is React.TouchEvent {
        return e && 'touches' in e;
    }

    function isMouseEvent(
        e: React.SyntheticEvent | Event
    ): e is React.MouseEvent {
        return e && 'clientX' in e;
    }

    interface IFirstTouch {
        clientX: number;
        clientY: number;
    }
    const getFirstTouch = (e: React.SyntheticEvent | Event): IFirstTouch => {
        if (isTouchEvent(e)) {
            return {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY,
            };
        } else if (isMouseEvent(e)) {
            return {
                clientX: e.clientX,
                clientY: e.clientY,
            };
        }
        return null;
    };
    const handleHorizontalBarClicked = (
        clickedPoint: IFirstTouch,
        tracker: MutableRefObject<HTMLDivElement>
    ) => {
        const trackerPosition = tracker.current.getBoundingClientRect();

        const innerClickedPoint = clickedPoint.clientX - trackerPosition.left;
        const relativePosition =
            (innerClickedPoint / trackerPosition.width) * 100;

        const newPosition =
            100 - Math.round(Math.max(0, Math.min(100, relativePosition)));

        let movingArm = SLIDER_CONTROLS.HORIZONTAL1;
        //decide which arm will move.
        if (
            getAbsDiff(positionRight, newPosition) <
            getAbsDiff(positionLeft, newPosition)
        ) {
            movingArm = SLIDER_CONTROLS.HORIZONTAL2;
        }

        if (movingArm === SLIDER_CONTROLS.HORIZONTAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionLeft));
            updateChanges(newPosition, positionLeft);
        } else {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionRight));
            updateChanges(newPosition, positionRight);
        }
    };
    const handleVerticalBarClicked = (
        clickedPoint: IFirstTouch,
        tracker: MutableRefObject<HTMLDivElement>
    ): void => {
        const trackerPosition = tracker.current.getBoundingClientRect();

        const innerClickedPoint = clickedPoint.clientY - trackerPosition.top;
        const relativePosition =
            (innerClickedPoint / trackerPosition.height) * 100;

        const newPosition =
            100 - Math.round(Math.max(0, Math.min(100, relativePosition)));
        let movingArm = SLIDER_CONTROLS.VERTICAL2;
        //decide which arm will move.
        if (
            getAbsDiff(positionBottom, newPosition) <
            getAbsDiff(positionTop, newPosition)
        ) {
            movingArm = SLIDER_CONTROLS.VERTICAL1;
        }

        if (movingArm === SLIDER_CONTROLS.VERTICAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionBottom));
            updateChanges(newPosition, positionBottom);
        } else {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, positionTop));
            updateChanges(newPosition, positionTop);
        }
    };
    const handleBarClicked = (e: PointerEvent | TouchEvent) => {
        e.preventDefault();
        const clickedPoint = getFirstTouch(e);
        if (!clickedPoint) return;
        if (vertical) {
            handleVerticalBarClicked(clickedPoint, trackerVerticalRef);
        } else {
            handleHorizontalBarClicked(clickedPoint, trackerRef);
        }
    };

    const handleTouchStart = (
        evt: PointerEvent | TouchEvent | React.FocusEvent<HTMLButtonElement>,
        inControl: string
    ) => {
        evt.stopPropagation();
        const touch = getFirstTouch(evt);
        edgeInControl.current = inControl;
        lastRight.current = positionRight;
        lastLeft.current = positionLeft;
        lastTop.current = positionTop;
        lastBottom.current = positionBottom;

        clientX.current = touch.clientX;
        clientY.current = touch.clientY;
    };

    const handleTouchMove = (e: Event) => {
        const touch = getFirstTouch(e);
        if (
            !touch ||
            !edgeInControl.current ||
            (!trackerVerticalRef.current && !trackerRef.current) // in re-render process and trackers still no avb.
        )
            return;
        // after eval the prev condition.
        // then this handler is the only one should handle this event.
        // need to be after check that it is for slider to ensure not blocking other events
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const inControl = edgeInControl.current;

        let moveDistance: number,
            moveDistanceReflection: number,
            newPosition: number;

        if (vertical) {
            moveDistance = clientY.current - touch.clientY;

            moveDistanceReflection =
                (moveDistance / trackerVerticalRef.current.offsetHeight) * 100;

            newPosition = Math.round(
                Math.max(
                    0,
                    Math.min(
                        100,
                        (inControl === SLIDER_CONTROLS.VERTICAL1
                            ? lastBottom.current
                            : lastTop.current) + moveDistanceReflection
                    )
                )
            );
        } else {
            moveDistance = clientX.current - touch.clientX;

            moveDistanceReflection =
                (moveDistance / trackerRef.current.offsetWidth) * 100;

            newPosition = Math.round(
                Math.max(
                    0,
                    Math.min(
                        100,
                        (inControl === SLIDER_CONTROLS.HORIZONTAL1
                            ? lastLeft.current
                            : lastRight.current) + moveDistanceReflection
                    )
                )
            );
        }

        if (inControl == SLIDER_CONTROLS.HORIZONTAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastLeft.current));
            updateChanges(positionLeft, newPosition);
        } else if (inControl == SLIDER_CONTROLS.HORIZONTAL1) {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastRight.current));
            updateChanges(newPosition, positionRight);
        } else if (inControl == SLIDER_CONTROLS.VERTICAL2) {
            updateMinArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastBottom.current));
            updateChanges(positionLeft, newPosition);
        } else if (inControl == SLIDER_CONTROLS.VERTICAL1) {
            updateMaxArm(newPosition);
            setMarkerSize(getAbsDiff(newPosition, lastTop.current));
            updateChanges(newPosition, positionRight);
        }
    };
    const handleTouchEnd = () => {
        edgeInControl.current = null;

        lastRight.current = null;
        lastLeft.current = null;

        lastTop.current = null;
        lastBottom.current = null;

        clientX.current = null;
        clientY.current = null;
    };

    return (
        <div
            id={`filter-${slugify(label)}`}
            className={
                'py-3 relative transition-[height] duration-[ease-out] delay-[0.5s]'
            }
            onTouchStart={e => {
                // this disables closing button sheet with drag down.
                e.stopPropagation();
            }}
        >
            <div className={'flex items-center justify-between mb-[22px]'}>
                <Typography className={'text-gray-700'} variant={'body2Medium'}>
                    {label}
                </Typography>
            </div>

            <div className={'flex flex-col h-[calc(80vh-200px)] lg:h-fit '}>
                <div
                    className={
                        'hidden lg:flex items-center justify-between h-5 min-h-5'
                    }
                >
                    <Typography className={'text-[1rem] font-medium'}>
                        {formatDisplayedValue(
                            getAbsVal(Math.min(positionRight, positionLeft))
                        )}
                    </Typography>
                    <Typography className={'text-[1rem] font-medium'}>
                        {formatDisplayedValue(
                            getAbsVal(Math.max(positionRight, positionLeft))
                        )}
                    </Typography>
                </div>
                <div
                    className={
                        'flex w-full lg:m-auto items-center h-full min-height-full lg:min-h-auto lg:h-fit justify-center p-5 pt-2'
                    }
                >
                    <div
                        className={
                            'py-1 relative min-h-full lg:min-h-[auto] min-w-full h-full'
                        }
                    >
                        <div
                            id={'trackerRef'}
                            ref={trackerRef}
                            onPointerUp={handleBarClicked}
                            className={`bg-gray-200 rounded-full min-h-auto h-2 ${
                                vertical ? ' hidden ' : ''
                            }`}
                        >
                            <div
                                className={
                                    'absolute h-2 rounded-full bg-primary-600 w-0'
                                }
                                style={{
                                    width: `${markerSize}%`,
                                    right: `${Math.min(
                                        positionLeft,
                                        positionRight
                                    )}%`,
                                }}
                            >
                                {' '}
                            </div>
                            <div
                                unselectable="on"
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL2
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL2
                                    )
                                }
                                //@ts-ignore
                                ref={rightHand}
                                style={{
                                    right: `${positionRight}%`,
                                }}
                                className={'absolute -mr-3 -top-0.5'}
                            >
                                <button
                                    className={
                                        'hover:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)]' +
                                        ' active:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)] ' +
                                        ' h-5 flex items-center justify-center w-5 ' +
                                        'rounded-full bg-white shadow border border-solid border-primary-600 ' +
                                        ' cursor-pointer'
                                    }
                                ></button>
                            </div>

                            <div
                                unselectable="on"
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL1
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.HORIZONTAL1
                                    )
                                }
                                ref={leftHand}
                                style={{
                                    right: `${positionLeft}%`,
                                }}
                                className={'absolute -mr-3 -top-0.5'}
                            >
                                <button
                                    className={
                                        'hover:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)]' +
                                        ' active:shadow-[0px_0px_0px_8px_rgba(0,155,93,0.16)] ' +
                                        ' h-5 flex items-center justify-center w-5 ' +
                                        'rounded-full bg-white shadow border border-solid border-primary-600 ' +
                                        ' cursor-pointer'
                                    }
                                ></button>
                            </div>
                        </div>
                        <div
                            id={'trackerRef-vertical'}
                            ref={trackerVerticalRef}
                            onPointerUp={handleBarClicked}
                            className={` bg-gray-200 rounded-full min-h-full  h-full w-3   mb-7 ${
                                !vertical ? ' hidden' : ''
                            }`}
                        >
                            <div
                                className={
                                    'absolute rounded-full bg-primary-600  w-3   '
                                }
                                style={{
                                    height: `${markerSize}%`,
                                    // height: '40%',
                                    bottom: `${Math.min(
                                        positionBottom,
                                        positionTop
                                    )}%`,
                                }}
                            >
                                {' '}
                            </div>
                            <div
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL2
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL2
                                    )
                                }
                                ref={leftHand}
                                style={{
                                    bottom: `${positionTop}%`,
                                }}
                                className={
                                    'absolute  p-2 ar:-right-3.5 en:-left-3.5 -mb-5'
                                }
                            >
                                <span
                                    className={'range-slider-vertical-control '}
                                >
                                    <input
                                        className={
                                            'range-slider-vertical-control-input'
                                        }
                                        aria-orientation="vertical"
                                        type="range"
                                        id="VERTICAL2"
                                        value="24"
                                        onChange={() => {
                                            //ignore event
                                            // this input to avoid touch events on safari.
                                        }}
                                        min="1"
                                        max="100"
                                    />

                                    <button
                                        className="range-slider-vertical-control-tool-tip-cont"
                                        role="tooltip"
                                    >
                                        <span className="range-slider-vertical-control-tool-tip-inner"></span>
                                        {formatDisplayedValue(
                                            getAbsVal(positionTop)
                                        )}
                                    </button>
                                </span>
                            </div>

                            <div
                                onPointerDown={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL1
                                    )
                                }
                                onTouchStart={evt =>
                                    handleTouchStart(
                                        evt,
                                        SLIDER_CONTROLS.VERTICAL1
                                    )
                                }
                                ref={leftHand}
                                style={{
                                    bottom: `${positionBottom}%`,
                                }}
                                className={
                                    'absolute  p-2 ar:-right-3.5 en:-left-3.5 -mb-5'
                                }
                            >
                                <span
                                    className={'range-slider-vertical-control '}
                                >
                                    <input
                                        className={
                                            'range-slider-vertical-control-input'
                                        }
                                        aria-orientation="vertical"
                                        type="range"
                                        id="VERTICAL1"
                                        value="24"
                                        onChange={() => {
                                            //ignore event
                                            // this input to avoid touch events on safari.
                                        }}
                                        min="1"
                                        max="100"
                                    />

                                    <button
                                        className="range-slider-vertical-control-tool-tip-cont"
                                        role="tooltip"
                                    >
                                        <span className="range-slider-vertical-control-tool-tip-inner"></span>
                                        {formatDisplayedValue(
                                            getAbsVal(positionBottom)
                                        )}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterGroupSlider;
